import React, {useEffect, useMemo, useState} from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Input,
  message,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import {CopyOutlined, DeleteOutlined, ExportOutlined, ShareAltOutlined} from '@ant-design/icons';
import dayjs, {Dayjs} from 'dayjs';
import {copyToClipboardAsync} from '../../helpers/clipboard';
import {useTranslation} from '../../hooks/useTranslation';
import {
  createDirectoryShare,
  DirectoryShare,
  DirectoryShareAccessMode,
  DirectoryShareExpiryMode,
  getDirectoryShare,
  revokeDirectoryShare,
  updateDirectoryShare,
} from '../../services/directoryShare';
import {buildImgCloudRouteUrl} from '../../utils/appRouting';
import './index.css';

const {Text} = Typography;

interface DirectoryShareManagerProps {
  directoryPath: string;
}

const DirectoryShareManager: React.FC<DirectoryShareManagerProps> = ({directoryPath}) => {
  const {t} = useTranslation();
  const [share, setShare] = useState<DirectoryShare | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [accessMode, setAccessMode] = useState<DirectoryShareAccessMode>('PUBLIC');
  const [password, setPassword] = useState('');
  const [expiryMode, setExpiryMode] = useState<DirectoryShareExpiryMode>('SEVEN_DAYS');
  const [customExpiry, setCustomExpiry] = useState<Dayjs | null>(null);

  const shareUrl = useMemo(
    () => share?.sharePath ? buildImgCloudRouteUrl(share.sharePath) : '',
    [share?.sharePath],
  );

  const loadShare = async () => {
    if (!directoryPath) return;
    setLoading(true);
    try {
      setShare(await getDirectoryShare(directoryPath));
    } catch (error) {
      setShare(null);
      message.error(error instanceof Error ? error.message : t('directoryShare.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShare();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directoryPath]);

  const resetForm = () => {
    const inactive = share?.status === 'EXPIRED' || share?.status === 'REVOKED';
    setAccessMode(share?.accessMode || 'PUBLIC');
    setPassword('');
    if (share && !inactive && !share.expiresAt) {
      setExpiryMode('PERMANENT');
      setCustomExpiry(null);
    } else if (share && !inactive && share.expiresAt) {
      setExpiryMode('CUSTOM');
      setCustomExpiry(dayjs(share.expiresAt));
    } else {
      setExpiryMode('SEVEN_DAYS');
      setCustomExpiry(null);
    }
  };

  const openSettings = () => {
    resetForm();
    setOpen(true);
  };

  const submit = async () => {
    if (accessMode === 'PASSWORD' && password && (password.length < 4 || password.length > 64)) {
      message.warning(t('directoryShare.passwordLength'));
      return;
    }
    if (accessMode === 'PASSWORD' && !password && !share?.passwordProtected) {
      message.warning(t('directoryShare.passwordRequired'));
      return;
    }
    if (expiryMode === 'CUSTOM' && (!customExpiry || !customExpiry.isAfter(dayjs()))) {
      message.warning(t('directoryShare.expiryRequired'));
      return;
    }
    const reactivating = !share || share.status === 'EXPIRED' || share.status === 'REVOKED';
    if (reactivating && accessMode === 'PASSWORD' && !password) {
      message.warning(t('directoryShare.passwordRequired'));
      return;
    }

    setSaving(true);
    try {
      const request = {
        directoryPath,
        accessMode,
        password: password || undefined,
        expiryMode,
        expiresAt: expiryMode === 'CUSTOM' ? customExpiry?.toISOString() : undefined,
      };
      const next = reactivating
        ? await createDirectoryShare(request)
        : await updateDirectoryShare(share.id, request);
      setShare(next);
      setOpen(false);
      window.dispatchEvent(new CustomEvent('imgcloud:directory-share-changed'));
      message.success(reactivating ? t('directoryShare.createSuccess') : t('directoryShare.updateSuccess'));
    } catch (error) {
      message.error(error instanceof Error ? error.message : t('directoryShare.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const revoke = async () => {
    if (!share) return;
    setSaving(true);
    try {
      await revokeDirectoryShare(share.id);
      setShare({...share, status: 'REVOKED'});
      window.dispatchEvent(new CustomEvent('imgcloud:directory-share-changed'));
      message.success(t('directoryShare.revokeSuccess'));
    } catch (error) {
      message.error(error instanceof Error ? error.message : t('directoryShare.revokeFailed'));
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    if (shareUrl) await copyToClipboardAsync(shareUrl, t('directoryShare.linkCopied'));
  };

  const statusTag = share ? (
    <Tag color={share.status === 'ACTIVE' ? 'success' : share.status === 'SUSPENDED_BY_VIP' ? 'warning' : 'default'}>
      {t(`directoryShare.status.${share.status}`)}
    </Tag>
  ) : null;

  return (
    <div className="directory-share-manager">
      <Space wrap>
        <Button
          type="primary"
          ghost
          icon={<ShareAltOutlined />}
          loading={loading}
          onClick={openSettings}
        >
          {share ? t('directoryShare.manage') : t('directoryShare.shareDirectory')}
        </Button>
        {statusTag}
        {share?.status === 'ACTIVE' && shareUrl && (
          <>
            <Button icon={<CopyOutlined />} onClick={copyLink}>{t('directoryShare.copyLink')}</Button>
            <Button
              icon={<ExportOutlined />}
              onClick={() => window.open(shareUrl, '_blank', 'noopener,noreferrer')}
            >
              {t('directoryShare.openLink')}
            </Button>
          </>
        )}
      </Space>

      {share?.status === 'SUSPENDED_BY_VIP' && (
        <Alert type="warning" showIcon message={t('directoryShare.vipSuspendedHint')} />
      )}

      <Modal
        title={share ? t('directoryShare.settingsTitle') : t('directoryShare.createTitle')}
        open={open}
        onCancel={() => setOpen(false)}
        footer={[
          share && share.status !== 'REVOKED' ? (
            <Popconfirm
              key="revoke"
              title={t('directoryShare.revokeConfirm')}
              okText={t('common.confirm')}
              cancelText={t('common.cancel')}
              onConfirm={revoke}
            >
              <Button danger icon={<DeleteOutlined />} loading={saving}>
                {t('directoryShare.revoke')}
              </Button>
            </Popconfirm>
          ) : null,
          <Button key="cancel" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>,
          share?.status !== 'SUSPENDED_BY_VIP' ? (
            <Button key="save" type="primary" loading={saving} onClick={submit}>
              {share?.status === 'EXPIRED' || share?.status === 'REVOKED'
                ? t('directoryShare.reshare')
                : t('common.confirm')}
            </Button>
          ) : null,
        ]}
      >
        <Space direction="vertical" size={16} style={{width: '100%'}}>
          <Alert
            type="info"
            showIcon
            message={t('directoryShare.liveExactTitle')}
            description={t('directoryShare.liveExactDescription')}
          />
          <div>
            <Text type="secondary">{t('directoryShare.accessMode')}</Text>
            <Radio.Group
              className="directory-share-manager__field"
              value={accessMode}
              onChange={(event) => setAccessMode(event.target.value)}
            >
              <Radio.Button value="PUBLIC">{t('directoryShare.passwordless')}</Radio.Button>
              <Radio.Button value="PASSWORD">{t('directoryShare.passwordProtected')}</Radio.Button>
            </Radio.Group>
          </div>
          {accessMode === 'PASSWORD' && (
            <Input.Password
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={share?.status === 'ACTIVE'
                ? t('directoryShare.passwordKeepPlaceholder')
                : t('directoryShare.passwordPlaceholder')}
              maxLength={64}
            />
          )}
          <div>
            <Text type="secondary">{t('directoryShare.expiry')}</Text>
            <Select
              className="directory-share-manager__field"
              value={expiryMode}
              onChange={setExpiryMode}
              options={[
                {value: 'ONE_DAY', label: t('directoryShare.expiryOptions.oneDay')},
                {value: 'SEVEN_DAYS', label: t('directoryShare.expiryOptions.sevenDays')},
                {value: 'THIRTY_DAYS', label: t('directoryShare.expiryOptions.thirtyDays')},
                {value: 'CUSTOM', label: t('directoryShare.expiryOptions.custom')},
                {value: 'PERMANENT', label: t('directoryShare.expiryOptions.permanent')},
              ]}
            />
          </div>
          {expiryMode === 'CUSTOM' && (
            <DatePicker
              showTime
              className="directory-share-manager__field"
              value={customExpiry}
              onChange={setCustomExpiry}
              disabledDate={(date) => date.endOf('day').isBefore(dayjs())}
            />
          )}
          <Text type="secondary">{t('directoryShare.permanentVipHint')}</Text>
          {shareUrl && share?.status === 'ACTIVE' && (
            <Input value={shareUrl} readOnly addonAfter={<CopyOutlined onClick={copyLink} />} />
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default DirectoryShareManager;
